import { PrimaryButton } from "@/components/StandardButton";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Shield, MessageSquare, Sparkles, ShieldCheck, Zap, Check, TrendingUp, Users, Clock, ArrowRight, AlertTriangle, Eye, UserX, Flame, ShieldAlert, MessagesSquare, Brain, Target, Heart, Scale, Lightbulb, BarChart3, Timer, Quote, Radar, DollarSign, Lock, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { OptimizedImage } from "@/components/ui/optimized-image";
import dashboardImage from "@/assets/dashboard-screenshot.png";
import testimonial1 from "@/assets/testimonial-1.jpg";
import testimonial2 from "@/assets/testimonial-2.jpg";
import testimonial3 from "@/assets/testimonial-3.jpg";
import { scrollToSection } from "@/lib/smoothScroll";
import { SEOHead } from "@/components/seo/SEOHead";
import { DisplayXL, DisplayL, SectionHeader, SubsectionHeader, BodyText, Caption } from "@/components/ui/typography";
import { SourcesCitation } from "@/components/marketing/SourcesCitation";
import { PricingPreview } from "@/components/marketing/PricingPreview";
import { ComparisonTable } from "@/components/marketing/ComparisonTable";
import { StickyCTA } from "@/components/marketing/StickyCTA";

const Index = () => {
  const navigate = useNavigate();

  // Handle hash navigation on page load
  useEffect(() => {
    if (window.location.hash) {
      const sectionId = window.location.hash.substring(1);
      scrollToSection(sectionId);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="SendFeedback.ai - 100% Anonymous Employee Feedback Platform" description="Build trust and catch retention risks early with truly anonymous employee feedback. AI-powered insights, 5-minute setup, no tracking. Start free." canonicalUrl="https://sendfeedback.ai" ogImage="/og-images/og-home.jpg" keywords="anonymous employee feedback, employee engagement, workplace feedback, HR software, employee surveys, retention risk, workplace culture" />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "SendFeedback - 100% Anonymous Employee Feedback Platform",
        "applicationCategory": "BusinessApplication",
        "description": "The only truly anonymous employee feedback platform. No names, no emails, no tracking—guaranteed privacy with optional department context for actionable insights.",
        "url": "https://sendfeedback.ai",
        "image": "https://sendfeedback.ai/dashboard-preview.png",
        "operatingSystem": "Web",
        "offers": [{
          "@type": "Offer",
          "name": "Starter Plan",
          "price": "39",
          "priceCurrency": "USD",
          "description": "100 anonymous submissions per month with basic AI sentiment analysis. 15-day free trial."
        }, {
          "@type": "Offer",
          "name": "Professional Plan",
          "price": "79",
          "priceCurrency": "USD",
          "description": "Unlimited anonymous submissions with advanced AI insights, HR guidance, and response templates"
        }, {
          "@type": "Offer",
          "name": "Business Plan",
          "price": "149",
          "priceCurrency": "USD",
          "description": "Unlimited pages and anonymous submissions with custom domain, white-label, and advanced HR support"
        }],
        "featureList": ["Guaranteed 100% anonymity - no exceptions", "Optional department tagging for context", "No login, no tracking, no IP collection", "AI-powered feedback clustering and sentiment analysis", "AI HR Support with risk classification and response templates", "Action tracker with kanban board for closing the loop", "Database-level enforcement prevents PII collection", "Legal guardrails and psychology-safe language guidance", "Export data in multiple formats"]
      })}
      </script>
      
      <Navigation />
      
      {/* Hero Section */}
      <section className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pt-6 sm:pt-12 pb-8 sm:pb-12 md:pt-16 md:pb-16">
        <div className="max-w-6xl mx-auto text-center space-y-4 sm:space-y-6">
          <DisplayXL className="text-foreground leading-tight sm:leading-normal px-2 sm:px-0">
            {/* Desktop: 3 lines */}
            <span className="hidden sm:block">Your Best People</span>
            <span className="hidden sm:block">Are <mark className="bg-black text-white px-2 py-0.5 rounded">Planning to Leave</mark></span>
            <span className="hidden sm:block"><mark className="bg-success/20 text-foreground px-2 py-0.5 rounded">Find Out Why</mark>—Before They Do</span>
            {/* Mobile: 4 lines */}
            <span className="block sm:hidden">Your Best People Are</span>
            <span className="block sm:hidden"><mark className="bg-black text-white px-2 py-0.5 rounded">Planning to Leave</mark></span>
            <span className="block sm:hidden"><mark className="bg-success/20 text-foreground px-2 py-0.5 rounded">Find Out Why</mark></span>
            <span className="block sm:hidden">Before They Do</span>
          </DisplayXL>
          
          <BodyText variant="large" className="text-muted-foreground max-w-3xl mx-auto px-2 sm:px-0">
            {/* Desktop: 2 lines */}
            <span className="hidden sm:block">Anonymous feedback surfaces the issues your team</span>
            <span className="hidden sm:block">won't say in meetings—so you can fix them first.</span>
            {/* Mobile: 3 lines */}
            <span className="block sm:hidden">Anonymous feedback surfaces</span>
            <span className="block sm:hidden">the issues your team won't</span>
            <span className="block sm:hidden">say in meetings.</span>
          </BodyText>
          
          <div className="flex flex-col items-center gap-3 sm:gap-4 md:gap-5 pt-2 sm:pt-2">
            <PrimaryButton size="lg" className="text-sm sm:text-base font-semibold w-auto px-6 py-3 sm:px-8 sm:py-3 min-h-[48px] touch-manipulation" onClick={() => navigate('/auth?mode=signup')} aria-label="Start your free 15-day trial of SendFeedback">
              Start Your Free 15-Day Trial
            </PrimaryButton>
            
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm text-muted-foreground px-2 sm:px-4">
              <span className="flex items-center gap-1.5 whitespace-nowrap justify-center">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                No card needed
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap justify-center">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                30-second setup
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap justify-center col-span-2 sm:col-span-1">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                Cancel anytime
              </span>
            </div>
          </div>
        </div>
        
        
        {/* Dashboard Screenshot */}
        <div className="max-w-6xl mx-auto mt-6 md:mt-8 lg:mt-12 xl:mt-16">
          <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-border/50 shadow-2xl overflow-hidden bg-muted/20 backdrop-blur-sm">
            <OptimizedImage src={dashboardImage} alt="SendFeedback dashboard with anonymous feedback analytics and AI insights" className="w-full h-auto" blurPlaceholder={true} />
          </div>
        </div>
      </section>
      
      {/* Social Proof Stats */}
      <section className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-6xl mx-auto space-y-8 md:space-y-12">
          <div className="text-center space-y-2 max-w-3xl mx-auto">
            <SectionHeader className="px-2 text-foreground">
              Turn employee silence into your competitive advantage
            </SectionHeader>
            <BodyText className="px-2 text-muted-foreground">When people can speak freely, you get the insights that actually matter.</BodyText>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center space-y-1.5 md:space-y-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-2 md:mb-3">
                <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">Anonymous</div>
              <BodyText variant="secondary" className="text-center">
                No tracking, no login—employees share what matters without fear
              </BodyText>
            </div>
            
            <div className="text-center space-y-1.5 md:space-y-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-2 md:mb-3">
                <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">AI-Powered</div>
              <BodyText variant="secondary" className="text-center">Sentiment analysis and pattern detection across all feedback</BodyText>
            </div>
            
            <div className="text-center space-y-1.5 md:space-y-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-2 md:mb-3">
                <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">Instant</div>
              <BodyText variant="secondary" className="text-center">
                Get actionable insights in seconds, not days of manual review
              </BodyText>
            </div>
            
            <div className="text-center space-y-1.5 md:space-y-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-2 md:mb-3">
                <ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 text-foreground" />
              </div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">Secure</div>
              <BodyText variant="secondary" className="text-center">
                Enterprise-grade privacy with GDPR compliance built-in
              </BodyText>
            </div>
          </div>
        </div>
      </section>


      {/* What You're Missing */}
      <section className="bg-muted/30 py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-8 md:space-y-12">
            <div className="text-center space-y-2 max-w-3xl mx-auto">
              <SectionHeader className="px-2 text-foreground">
                Right now, your team knows exactly what's broken. Do you?
              </SectionHeader>
              <BodyText className="px-2 text-muted-foreground">
                Every day without anonymous feedback is another day of silent problems becoming expensive crises
              </BodyText>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
              <Card className="border-border bg-card p-6 md:p-8 space-y-3">
              <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-2">
                <UserX className="h-6 w-6 text-destructive" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold">
                  Your Best People Are Already Gone (Mentally)
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Top performers quit 6-12 months before resigning. Anonymous feedback catches this—surveys don't.
                </p>
              </Card>

              <Card className="border-border bg-card p-6 md:p-8 space-y-3">
              <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-2">
                <ShieldAlert className="h-6 w-6 text-destructive" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold">Toxic Managers Hiding in Plain Sight</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  <a href="https://www.gallup.com/workplace/232955/no-employee-benefit-no-one-talking.aspx" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">50% of employees leave because of their manager</a> (Gallup). No one tells you until they're interviewing elsewhere.
                </p>
              </Card>

              <Card className="border-border bg-card p-6 md:p-8 space-y-3">
              <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-2">
                <Flame className="h-6 w-6 text-destructive" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold">Culture Issues Brewing in Silence</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  DEI concerns, burnout, unclear expectations—these fester until it's too late to fix.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-6xl mx-auto space-y-8 md:space-y-12">
          <div className="text-center space-y-2 max-w-3xl mx-auto">
            <SectionHeader className="px-2 text-foreground">Why Teams Choose<br className="sm:hidden" /> Anonymous Feedback</SectionHeader>
            <BodyText className="px-2 text-muted-foreground">Build trust, catch problems early, and make decisions based on truth—not assumptions.</BodyText>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
            <Card className="border-border bg-card p-6 md:p-8 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-success/20 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold">True Anonymity = True Honesty</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                No login, no tracking, no IP addresses. Legal-grade anonymity gets you truth about harassment and toxic behavior before lawyers get involved.
              </p>
            </Card>

            <Card className="border-border bg-card p-6 md:p-8 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-success/20 flex items-center justify-center">
                <Brain className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold">Never Say the Wrong Thing in Sensitive Situations</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                Get HR-safe response templates in 3 tones (empathetic, professional, direct) with legal compliance guidance for harassment, mental health, and discrimination issues.
              </p>
            </Card>

            <Card className="border-border bg-card p-6 md:p-8 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-success/20 flex items-center justify-center">
                <Radar className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold">Catch Flight Risks 6 Months Early (Not 6 Days Before They Quit)</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                Automatically spot patterns in feedback, track sentiment changes, and surface urgent issues. Know when "just venting" is actually "about to quit."
              </p>
            </Card>

            <Card className="border-border bg-card p-6 md:p-8 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-success/20 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold">Instant Alerts for High-Risk Issues (Harassment, Safety, Legal)</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                Get notified immediately for harassment, safety concerns, or legal issues. Act fast when it matters most.
              </p>
            </Card>

            <Card className="border-border bg-card p-6 md:p-8 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-success/20 flex items-center justify-center">
                <Scale className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold">Built-In HR Advisor for Sensitive Situations</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                AI-powered HR guidance for harassment claims, mental health concerns, legal risks. Know what to say and what not to.
              </p>
            </Card>

            <Card className="border-border bg-card p-6 md:p-8 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-success/20 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold">Spend Time Fixing, Not Analyzing (AI Does the Work)</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                Weekly summaries, trend reports, AI-generated recommendations. See what matters in seconds.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Anonymous-Only Section */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-2 mb-10">
            <SectionHeader className="text-foreground">
              Why We're Anonymous-Only
            </SectionHeader>
            <BodyText className="text-muted-foreground">We don't offer "optional" anonymity because true honesty requires guaranteed privacy.</BodyText>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-border bg-card p-6 md:p-8 space-y-3">
              <div className="w-12 h-12 rounded-xl bg-success/20 flex items-center justify-center">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold">No Compromise on Privacy</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                Other platforms offer "optional" anonymity, which creates fear that management might 
                track who chooses to be anonymous. With us, everyone is always anonymous—no exceptions, 
                no tracking, no backdoors.
              </p>
            </Card>
            <Card className="border-border bg-card p-6 md:p-8 space-y-3">
              <div className="w-12 h-12 rounded-xl bg-success/20 flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold">Higher Response Quality</h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                When employees know they're truly anonymous, they share the real issues. Optional 
                department tagging gives context without compromising safety.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-muted/30 py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-2 mb-10 md:mb-14">
              <SectionHeader className="px-2 text-foreground">
                Problems caught. Turnover prevented.
              </SectionHeader>
              <BodyText className="px-2 text-muted-foreground">
                Real feedback from real teams that changed outcomes.
              </BodyText>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <Card className="border-border bg-card p-6 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <h3 className="text-lg font-semibold">Retention Crisis Prevention</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  "Leadership keeps taking credit for our work" revealed 3 months before a key team member planned to quit. Fixed the issue. They stayed.
                </p>
              </Card>
              
              <Card className="border-border bg-card p-6 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <h3 className="text-lg font-semibold">DEI Reality Check</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  "Women in our team get interrupted 3x more in meetings" led to leadership coaching that significantly improved culture scores.
                </p>
              </Card>
              
              <Card className="border-border bg-card p-6 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <h3 className="text-lg font-semibold">Remote Team Connection</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  "I feel invisible working from home" sparked changes that dramatically increased remote engagement and prevented turnover.
                </p>
              </Card>
              
              <Card className="border-border bg-card p-6 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <h3 className="text-lg font-semibold">Manager Blind Spots</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  "My manager plays favorites and it's destroying morale" was caught before an entire team's turnover. Manager coaching implemented immediately.
                </p>
              </Card>
              
              <Card className="border-border bg-card p-6 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <h3 className="text-lg font-semibold">Policy Impact Assessment</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  "The new PTO policy feels like a punishment" saved HR from a disastrous rollout. Policy adjusted before official launch.
                </p>
              </Card>
              
              <Card className="border-border bg-card p-6 space-y-3 hover:shadow-lg hover:border-primary/20 transition-all duration-300">
                <h3 className="text-lg font-semibold">Innovation Blockers</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  "Our approval process kills creativity" led to process changes that significantly accelerated product velocity within months.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>


      {/* How It Works */}
      <section id="how-it-works" className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-6xl mx-auto space-y-8 md:space-y-12">
          <div className="text-center space-y-2 max-w-3xl mx-auto">
            <SectionHeader className="px-2 text-foreground">
              How it works
            </SectionHeader>
            <BodyText className="px-2 text-muted-foreground">Three simple steps to unlock honest feedback from your team.</BodyText>
          </div>
          
          <div className="space-y-6 md:space-y-8">
            <div className="flex gap-4 md:gap-6 items-start">
              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-bold text-base md:text-lg">
                1
              </div>
              <div className="space-y-1.5 md:space-y-2 pt-0.5 md:pt-1">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold">Create your anonymous feedback page</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Set up a custom page in 30 seconds. Share the link with your team via Slack, email, or QR code. No accounts or setup required.
                </p>
              </div>
            </div>

            <div className="flex gap-4 md:gap-6 items-start">
              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-bold text-base md:text-lg">
                2
              </div>
              <div className="space-y-1.5 md:space-y-2 pt-0.5 md:pt-1">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold">Team submits feedback anonymously</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Employees share honest input, attach files, and express what's really happening — all with complete anonymity protection.
                </p>
              </div>
            </div>

            <div className="flex gap-4 md:gap-6 items-start">
              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-bold text-base md:text-lg">
                3
              </div>
              <div className="space-y-1.5 md:space-y-2 pt-0.5 md:pt-1">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold">AI analyzes and you respond privately</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Get instant AI summaries and sentiment analysis. Post responses that submitters can view anonymously via their tracking code.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-10 md:mt-14 p-6 md:p-8 bg-primary/5 border border-primary/20 rounded-xl md:rounded-2xl">
            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-4 px-2">
              The average company using SendFeedback:
            </h3>
            <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
              <div className="flex items-start gap-2">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm md:text-base">Catches culture issues months earlier than traditional surveys</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm md:text-base">Helps prevent regrettable departures before they happen</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm md:text-base">Saves hours on manual feedback analysis with AI summaries</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm md:text-base">Higher participation than traditional engagement surveys</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm md:text-base">Identifies manager issues before they trigger team departures</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm md:text-base">Helps improve employee retention by addressing real concerns</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <ComparisonTable />

      {/* Social Proof */}
      <section className="container mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center space-y-2 mb-10 md:mb-14">
            <SectionHeader className="px-2 text-foreground">
              Real results from real companies
            </SectionHeader>
            <BodyText className="px-2 text-muted-foreground">Leaders who solved critical culture issues before they became expensive.</BodyText>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <Card className="border-border bg-card hover:shadow-lg hover:border-primary/20 transition-all duration-300">
              <CardContent className="pt-6 pb-6 space-y-4">
                <Quote className="h-8 w-8 text-primary/30" />
                <p className="text-muted-foreground leading-relaxed text-base">
                  We caught a manager bullying situation that would've cost us 5 team members and a discrimination lawsuit. ROI in month one.
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <img src={testimonial1} alt="Sarah Mitchell" className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold text-foreground">Sarah Mitchell</div>
                    <div className="text-xs text-muted-foreground">Head of People</div>
                    <div className="text-xs text-muted-foreground">CloudScale Software</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-border bg-card hover:shadow-lg hover:border-primary/20 transition-all duration-300">
              <CardContent className="pt-6 pb-6 space-y-4">
                <Quote className="h-8 w-8 text-primary/30" />
                <p className="text-muted-foreground leading-relaxed text-base">
                  Our retention improved significantly after we started using this. Turns out people will stay when they feel heard—and safe.
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <img src={testimonial2} alt="James Cooper" className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold text-foreground">James Cooper</div>
                    <div className="text-xs text-muted-foreground">CEO</div>
                    <div className="text-xs text-muted-foreground">RemoteFirst HQ</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-border bg-card hover:shadow-lg hover:border-primary/20 transition-all duration-300">
              <CardContent className="pt-6 pb-6 space-y-4">
                <Quote className="h-8 w-8 text-primary/30" />
                <p className="text-muted-foreground leading-relaxed text-base">
                  Finally found out why our best engineers were leaving. It wasn't salary—it was a toxic product manager. We fixed it. They stayed.
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <img src={testimonial3} alt="David Chen" className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold text-foreground">David Chen</div>
                    <div className="text-xs text-muted-foreground">VP of Engineering</div>
                    <div className="text-xs text-muted-foreground">PayStream Technologies</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <SourcesCitation className="max-w-4xl mx-auto" />
        </div>
      </section>

      {/* Pricing Preview */}
      <PricingPreview />

      {/* FAQ */}
      <section id="faq" className="bg-muted/40 py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center space-y-2 mb-8 md:mb-10 lg:mb-12">
              <SectionHeader className="px-2 text-foreground">
                Questions leaders ask
              </SectionHeader>
              <BodyText className="px-2 text-muted-foreground">
                <span className="sm:hidden">Everything you need to know<br />before getting started.</span>
                <span className="hidden sm:inline">Everything you need to know before getting started.</span>
              </BodyText>
            </div>
            
            <Accordion type="single" collapsible className="space-y-3">
              {/* ROI */}
              <AccordionItem value="faq-1" className="border-0">
                <Card className="border-border hover:shadow-md hover:border-primary/20 transition-all duration-200">
                  <AccordionTrigger className="px-5 py-4 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    <div className="flex items-center gap-3 text-left">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <DollarSign className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-semibold text-sm md:text-base">What's the ROI?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 pt-0">
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed pl-12">
                      If this tool prevents even one regrettable departure, you've saved significant costs—SHRM estimates turnover costs 50-200% of annual salary in recruiting, training, and lost productivity. Plus: hours saved monthly in feedback analysis, earlier problem detection, and better decision-making data.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              {/* Problems */}
              <AccordionItem value="faq-2" className="border-0">
                <Card className="border-border hover:shadow-md hover:border-primary/20 transition-all duration-200">
                  <AccordionTrigger className="px-5 py-4 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    <div className="flex items-center gap-3 text-left">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <AlertTriangle className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-semibold text-sm md:text-base">Won't this create more problems than it solves?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 pt-0">
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed pl-12">
                      The opposite. You already have these problems—you just don't see them yet. Anonymous feedback surfaces issues while they're still fixable. Ignoring them doesn't make them go away; it makes them expensive.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              {/* Engagement surveys */}
              <AccordionItem value="faq-3" className="border-0">
                <Card className="border-border hover:shadow-md hover:border-primary/20 transition-all duration-200">
                  <AccordionTrigger className="px-5 py-4 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    <div className="flex items-center gap-3 text-left">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <Zap className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-semibold text-sm md:text-base">How is this different from engagement surveys?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 pt-0">
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed pl-12">
                      Engagement surveys are like yearly physicals—they catch what's already wrong. SendFeedback is a continuous health monitor that spots issues while they're still treatable. Anonymous feedback gets significantly higher participation because employees feel safe to share openly.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              {/* Abuse */}
              <AccordionItem value="faq-4" className="border-0">
                <Card className="border-border hover:shadow-md hover:border-primary/20 transition-all duration-200">
                  <AccordionTrigger className="px-5 py-4 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    <div className="flex items-center gap-3 text-left">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <Shield className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-semibold text-sm md:text-base">What if people abuse anonymity?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 pt-0">
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed pl-12">
                      Our AI-powered abuse detection and PII filtering keeps feedback constructive. People want to help, not hurt—when they have a safe channel.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              {/* Anonymity protection */}
              <AccordionItem value="faq-5" className="border-0">
                <Card className="border-border hover:shadow-md hover:border-primary/20 transition-all duration-200">
                  <AccordionTrigger className="px-5 py-4 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    <div className="flex items-center gap-3 text-left">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <Lock className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-semibold text-sm md:text-base">How is anonymity actually protected?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 pt-0">
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed pl-12">
                      We don't collect IP addresses, browser fingerprints, login credentials, or any identifying metadata. Even we can't tell who submitted what. It's legal-grade protection that employees actually trust.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              {/* Data export */}
              <AccordionItem value="faq-6" className="border-0">
                <Card className="border-border hover:shadow-md hover:border-primary/20 transition-all duration-200">
                  <AccordionTrigger className="px-5 py-4 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    <div className="flex items-center gap-3 text-left">
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <Download className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-semibold text-sm md:text-base">Can we export and own our data?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 pt-0">
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed pl-12">
                      Yes. Export feedback, summaries, and insights anytime in multiple formats. You own your data completely—no lock-in, no limitations.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container mx-auto px-4 md:px-6 lg:px-8 py-12 sm:py-16 md:py-24 lg:py-32">
        <div className="max-w-3xl mx-auto text-center space-y-6 sm:space-y-8">
          
          {/* Headline */}
          <div className="space-y-3 sm:space-y-4 px-2 sm:px-0">
            <DisplayL className="text-foreground leading-tight">
              {/* Desktop: 2 lines */}
              <span className="hidden sm:block">Stop Losing Your Best People to</span>
              <span className="hidden sm:block">Problems You Could Fix</span>
              {/* Mobile: 3 lines */}
              <span className="block sm:hidden">Stop Losing Your Best</span>
              <span className="block sm:hidden">People to Problems</span>
              <span className="block sm:hidden">You Could Fix</span>
            </DisplayL>
            
            <BodyText variant="large" className="text-muted-foreground max-w-2xl mx-auto px-2 sm:px-0">
              Your team is already thinking about leaving—you just don't know it yet. Start your trial and catch retention risks early.
            </BodyText>
          </div>
          
          {/* CTA */}
          <div className="flex flex-col items-center gap-4 sm:gap-6 pt-2 sm:pt-4">
            <PrimaryButton size="lg" className="group text-sm sm:text-lg px-5 py-3 sm:px-8 sm:py-6 rounded-lg hover:scale-105 transition-transform w-full sm:w-auto max-w-sm" onClick={() => navigate('/auth?mode=signup')} aria-label="Start your free trial in 30 seconds">
              Start in 30 Seconds
              <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </PrimaryButton>
            
            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm text-muted-foreground px-4">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                <span className="bg-success/20 px-1.5 py-0.5 rounded">15-day free trial</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                <span className="bg-success/20 px-1.5 py-0.5 rounded">No card needed</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                <span className="bg-success/20 px-1.5 py-0.5 rounded">Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Sticky CTA */}
      <StickyCTA />
    </div>
  );
};

export default Index;